// src/components/layout/Sidebar.tsx
import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { clearToken, clearUser } from "../../utils/storage";
import toast from "react-hot-toast";
import Modal from "../Modal";
import { useAuth } from "../../context/AuthContext";
import Logo from '../../assets/images/Logo.png';

type Props = {
  compact?: boolean;          // collapse on desktop
  onClose?: () => void;       // called when mobile drawer should close
  isMobile?: boolean;         // whether rendering as mobile drawer
  onToggleCompact?: () => void;
};

const topItems = [
  { to: "/dashboard", label: "Dashboard", icon: "mdi:view-dashboard-outline", title: "Dashboard", roles: ["Admin", "Employee"] },
  { to: "/employees", label: "Employees", icon: "mdi:account-group-outline", title: "Employees", roles: ["Admin"] },
  { to: "/customers", label: "Customers", icon: "mdi:account-outline", title: "Customers", roles: ["Admin", "Employee"] },
  { to: "/customers/details", label: "Home", icon: "mdi:view-dashboard-outline", title: "CustomersInfo", roles: ["Customer"] },
];

const bottomItems = [
  { to: "/vehicles", label: "Vehicles", icon: "mdi:car-outline", title: "Vehicles", roles: ["Admin", "Employee"] },
  { to: "/payments", label: "Payments", icon: "mdi:bank-outline", title: "Payments", roles: ["Admin", "Employee"] },
  { to: "/transactions", label: "Transactions", icon: "hugeicons:transaction-history", title: "Transactions", roles: ["Admin", "Employee"] },
  { to: "/penalties", label: "Penalties", icon: "fa7-solid:sort-amount-up-alt", title: "Penalties", roles: ["Admin"] },
  { to: "/notifications", label: "Notifications", icon: "si:notifications-alt-2-line", title: "Notifications", roles: ["Admin", "Employee"] },
  { to: "/report", label: "Report", icon: "streamline-plump:file-report-remix", title: "Report", roles: ["Admin", "Employee"] },
];


const Sidebar: React.FC<Props> = ({ compact = false, onClose, isMobile = false, onToggleCompact }) => {
  const { user , logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loansOpen, setLoansOpen] = useState(false);


  const toggleLoans = () => {
    setLoansOpen((prev) => !prev);
  };


  const navItem = (isActive: boolean) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive ? "bg-[#002949] text-white" : "text-gray-700 hover:bg-gray-100"
    }`;

  // open confirm dialog
  const handleLogoutClick = () => {
    if (isMobile && onClose) {
      onClose();
      // small timeout to ensure closing animation begins -- optional
      setTimeout(() => setConfirmOpen(true), 80);
    } else {
      setConfirmOpen(true);
    }
  };

  // user confirms logout
  const confirmLogout = () => {
    logout();
    setConfirmOpen(false);
    toast.success("Logged out successfully ");
    clearToken();
    clearUser();
    navigate("/", { replace: true });
    onClose?.();
  };

  // user cancels logout
  const cancelLogout = () => {
    setConfirmOpen(false);
  };
  const handleProfileClick = () => {
    navigate("/profile");
    if (isMobile && onClose) onClose();
  };
  const location = useLocation();
  React.useEffect(() => {
    if (location.pathname.startsWith("/loans") || location.pathname === "/loan-create" || location.pathname === "/loan-dues") {
      setLoansOpen(true);
    }
  }, [location.pathname]);
  // On mobile drawer, clicking a nav should close the drawer (call onClose)
  return (
    <div
      className={`flex flex-col h-full bg-white  shadow-md rounded-xl overflow-hidden transition-[width] scrollbar-hide duration-300 ease-in-out
    ${isMobile ? "w-64" : compact ? "w-25 " : "w-64 "}`} style={{ willChange: "width" }}
    >
      <div className="flex items-center gap-3 p-2 border-b cursor-pointer" onClick={handleProfileClick}>

        <div className="flex items-center gap-3">
          <img
            src={Logo}
            alt="Logo"
            className="w-10 h-10 "
          />
        </div>

        {!compact && (
          <div>
            <div className="font-semibold text-sm">Hi, {user?.username || "User"}</div>
            <div className="text-xs text-gray-500">{user?.email || "Email"}</div>
          </div>
        )}

        {/* hide close icon on desktop */}
        {isMobile && (
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="ml-auto p-2 rounded-md hover:bg-gray-100"
          >
            <Icon icon="mdi:close" width="18" />
          </button>
        )}
      </div>

      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto overflow-x-hidden scrollbar-hide ">

        {/* Top section */}
        {topItems
          .filter((it) => !it.roles || it.roles.includes(user?.role))
          .map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              title={it.title}
              className={({ isActive }) =>
                `${navItem(isActive)} ${(compact && !isMobile) ? "justify-center" : ""}`
              }
              onClick={() => {
                if (isMobile && onClose) onClose();
              }}
            >
              <Icon icon={it.icon} width="20" />
              {!compact && <span className="text-sm">{it.label}</span>}
            </NavLink>
          ))}


        {/* Loans Dropdown */}
        {(user?.role === "Admin" || user?.role === "Employee") && (<div>
          <button
            onClick={toggleLoans}
            className={`${navItem(false)} w-full ${(compact && !isMobile) ? "justify-center" : ""}`}
          >
            <Icon icon="mdi:currency-inr" width="20" />
            {!compact && (
              <>
                <span className="text-sm flex-1 text-left">Loans</span>
                <Icon
                  icon={loansOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                  width="18"
                  className="ml-auto"
                />
              </>
            )}
          </button>

          {/* Animated submenu */}
          <div
            className={`ml-8 overflow-hidden transition-all duration-300 ease-in-out
      ${loansOpen && !compact ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}
    `}
          >
            <NavLink
              to="/loans"
              className={({ isActive }) =>
                `${navItem(isActive)} text-sm py-1 px-2 rounded-md`
              }
              onClick={() => {
                if (isMobile && onClose) onClose();
                setLoansOpen(false);
              }}
            ><Icon
                icon="pajamas:stage-all"
              />
              All Loans
            </NavLink>
            <NavLink
              to="/loan-create"
              className={({ isActive }) =>
                `${navItem(isActive)} text-sm py-1 px-2 rounded-md`
              }
              onClick={() => {
                if (isMobile && onClose) onClose();
                setLoansOpen(false);
              }}
            >
              <Icon
                icon="carbon:create-link"
              />
              Create Loan
            </NavLink>
          </div>
        </div>)}


        {/* Bottom section */}
        {bottomItems
          .filter((it) => !it.roles || it.roles.includes(user?.role))
          .map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              title={it.title}
              className={({ isActive }) =>
                `${navItem(isActive)} ${(compact && !isMobile) ? "justify-center" : ""}`
              }
              onClick={() => {
                if (isMobile && onClose) onClose();
              }}
            >
              <Icon icon={it.icon} width="20" />
              {!compact && <span className="text-sm">{it.label}</span>}
            </NavLink>
          ))}

      </nav>

      {/* Collapse/Expand button for desktop */}
      {!isMobile && (
        <div className="hidden md:inline-flex  items-end justify-end">
          <button
            onClick={onToggleCompact}
            className={` gap-2  py-3 px-2  rounded-2xl hover:bg-white  cursor-pointer -mr-2 bg-white border-r-2 border-gray-200 transition-all duration-300 ${compact ? "hover:translate-x-1 transition-all duration-300 ease-in-out" : "hover:-translate-x-0.5 transition-all duration-300 ease-in-out"} `}
            aria-label="Toggle sidebar" title="sidebar toggle"
          >
            <Icon icon={compact ? "lets-icons:expand-right-double" : "lets-icons:expand-left-double"} width="18" />
          </button>
        </div>
      )}

      <div className="p-2 ">
        <button
          onClick={handleLogoutClick}
          className={`w-full flex items-center justify-center    py-2 rounded-xl text-white font-semibold bg-red-500  ease-in-out cursor-pointer`}
        >
          <Icon icon="mdi:logout" width="18" />

          <span
            className={`transition-opacity duration-300 ease-in-out ${compact && !isMobile ? "opacity-0 w-0 overflow-hidden " : "opacity-100 w-auto  pl-2"
              }`}
          >
            Logout
          </span>
        </button>
      </div>
      {/* Confirmation modal */}
      <Modal isOpen={confirmOpen} onClose={cancelLogout} title="Confirm Logout">
        <p className="mb-4">Are you sure you want to logout?</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={cancelLogout}
            className="px-4 py-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={confirmLogout}
            className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600"
          >
            Logout
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default Sidebar;
